import { Colors } from '@vetahealth/fishing-gear/colors'
import { Info } from '@vetahealth/tuna-can-api'
import { Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import styled, { useTheme } from 'styled-components'

import packageJson from '../../../package.json'
import { API } from '../../lib/api'
import { AuthStep, useAuthStore } from '../../stores/auth'
import { ProsperLogoWordmark } from '../Svg/ProsperLogoWordmark'
import { ForgotPassword } from './ForgotPassword'
import { ResetPassword } from './ResetPassword'
import { Signin } from './Signin'
import { TwoFactorResetPassword } from './TwoFactorResetPassword'
import { TwoFactorSignin } from './TwoFactorSignin'

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  background: ${Colors.mauve900};
`
const Card = styled.div`
  background: ${({ theme }) => theme.quinaryBackground};
  border-radius: 6px;
`
const Section = styled.div`
  padding: 40px 60px;
`
const ProsperCareLogo = styled.div`
  padding: 60px 60px 0 60px;
  width: 45%;
`
const InfoText = styled(Typography.Text)`
  position: absolute;
  bottom: 8px;
  right: 8px;
  font-size: 11px;
  color: ${Colors.mauve600};
  text-align: right;
`

export function Auth(): React.ReactNode {
  const { t } = useTranslation()
  const theme = useTheme()
  const [step, setStep, resetSoft] = useAuthStore((state) => [state.step, state.setStep, state.resetSoft])
  const [info, setInfo] = useState<Info>()
  const location = useLocation()

  const query = new URLSearchParams(location.search)
  const resetPasswordCode = query.get('resetPassword')
  const isInitialPasswordSetup = query.get('initial') === 'true'
  const twoFactorEmail = query.get('twoFactorEmail')

  useEffect(() => {
    API.getInfo().then((serverInfo) => {
      if (serverInfo) setInfo(serverInfo)
    })

    return resetSoft
  }, [])

  useEffect(() => {
    if (resetPasswordCode) {
      if (twoFactorEmail) {
        setStep(AuthStep.TWO_FACTOR_RESET_PASSWORD)
      } else {
        setStep(AuthStep.RESET_PASSWORD)
      }
    }
  }, [resetPasswordCode, twoFactorEmail])

  const CurrentStep: React.ElementType = {
    [AuthStep.SIGNIN]: Signin,
    [AuthStep.TWO_FACTOR_SIGNIN]: TwoFactorSignin,
    [AuthStep.TWO_FACTOR_RESET_PASSWORD]: TwoFactorResetPassword,
    [AuthStep.FORGOT_PASSWORD]: ForgotPassword,
    [AuthStep.RESET_PASSWORD]: ResetPassword,
  }[step]

  const serverVersion = info?.serverVersion.includes('beta')
    ? `${info?.serverVersion} (${info?.serverGithubSha})`
    : (info?.serverVersion ?? '')

  return (
    <Wrapper>
      <InfoText>
        <div>{t('version.client', { version: packageJson.version })}</div>
        <div>{t('version.server', { version: serverVersion })}</div>
      </InfoText>
      <div>
        <Card>
          <ProsperCareLogo>
            <ProsperLogoWordmark width="100%" height="100%" color={theme.logo} aria-label="Prosper" />
          </ProsperCareLogo>
          <Section>
            <CurrentStep resetCode={resetPasswordCode} isInitial={isInitialPasswordSetup} email={twoFactorEmail} />
          </Section>
        </Card>
      </div>
    </Wrapper>
  )
}
